<template>
  <div class="columns is-centered is-multiline">
    <div class="column is-two-thirds">
      <div class="watermark-section">
        <h4 class="title is-4">Watermark</h4>
        <div
          class="file has-name is-full is-primary"
          :class="{
            disabled: !watermarkValue || disableWatermark
          }"
        >
          <label class="file-label">
            <input
              ref="watermark"
              class="file-input"
              accept="image/png"
              type="file"
              name="watermark"
              :disabled="!watermarkValue || disableWatermark"
              :class="{
                disabled: !watermarkValue || disableWatermark
              }"
              @change="validateImage('watermark')"
            />
            <span class="file-cta">
              <span class="file-icon">
                <i class="fas fa-upload"></i>
              </span>
              <span class="file-label">Upload Watermark...</span>
            </span>
            <span class="file-name">
              {{ user.account.watermark || 'Watermark file' }}
            </span>
            <div
              class="is-inline-block buttons has-addons"
              style="margin-left: 0.75rem"
            >
              <button
                class="button is-danger tooltip is-tooltip-top"
                data-tooltip="Delete Mark"
                :disabled="!user.account.watermark"
                @click="openDeleteConfirmation($event, 'watermark')"
              >
                <span class="icon">
                  <i class="fas fa-trash-alt"></i>
                </span>
              </button>
            </div>
          </label>
        </div>
        <span class="is-size-7">* Watermark must be a PNG image.</span>
        <div class="columns preview-container" v-if="user.account.watermark">
          <div class="column is-half">
            <h5 class="title is-5">Original</h5>
            <img :src="user.account.watermark" alt="Original image" />
          </div>
          <div class="column is-half">
            <h5 class="title is-5">Preview</h5>
            <img
              :src="user.account.watermark"
              class="watermark-preview"
              alt="Watermark image"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="column is-full">
      <div class="is-divider"></div>
    </div>
    <div class="column is-two-thirds">
      <div class="branding-section">
        <h4 class="title is-4">Branding</h4>
        <label class="label">Brand Logo</label>
        <div
          class="file has-name is-full is-primary"
          :class="{
            disabled: !brandingValue || disableBranding
          }"
        >
          <label class="file-label">
            <input
              ref="branding"
              class="file-input"
              accept="image/jpg, image/jpeg, image/png"
              type="file"
              name="branding"
              :disabled="!brandingValue || disableBranding"
              :class="{
                disabled: !brandingValue || disableBranding
              }"
              @change="validateImage('branding')"
            />
            <span class="file-cta">
              <span class="file-icon">
                <i class="fas fa-upload"></i>
              </span>
              <span class="file-label">Upload Logo...</span>
            </span>
            <span class="file-name">
              {{ logo || 'Branding file' }}
            </span>
            <div
              class="is-inline-block buttons has-addons"
              style="margin-left: 0.75rem"
            >
              <button
                class="button is-danger tooltip is-tooltip-top"
                data-tooltip="Delete Logo"
                :disabled="!logo"
                @click="openDeleteConfirmation($event, 'branding')"
              >
                <span class="icon">
                  <i class="fas fa-trash-alt"></i>
                </span>
              </button>
            </div>
          </label>
        </div>
        <span class="is-size-7">
          {{
            `* Logo dimensions must be lower than ${logoMaxWidth}px by ${logoMaxHeight}px.`
          }}
        </span>
        <div class="field">
          <div class="field">
            <ValidationObserver ref="observer" v-slot="{ invalid }">
              <label class="label">Custom Logo URL</label>
              <ValidationProvider
                name="Custom URL"
                rules="url"
                v-slot="{ errors }"
              >
                <!-- @input force the lowerCase without the infinite loop warning -->
                <input
                  type="text"
                  class="input"
                  :class="{ 'is-danger': errors.length }"
                  v-model="customURL"
                  placeholder="https://example.com"
                  :disabled="!logo"
                  @input="customURL = $event.target.value.toLowerCase()"
                />
                <p class="help is-danger" v-show="errors.length">
                  {{ errors[0] }}
                </p>
              </ValidationProvider>
              <div class="columns" style="margin-top: 0px">
                <div class="column is-4">
                  <button
                    class="button is-primary"
                    :disabled="invalid || !isDifferentURL || loading"
                    @click="updateCustomURL"
                  >
                    Update URL
                  </button>
                </div>
              </div>
            </ValidationObserver>
          </div>
        </div>
        <span class="is-size-7">
          In case you want to redirect someone by clicking on your logo
        </span>
        <div class="field">
          <label class="label">Heading Bar Color</label>
          <div class="field">
            <div class="control has-icons-left">
              <color-picker
                v-model="brandingColor"
                :disabled="!brandingValue"
              />
            </div>
          </div>
          <div class="field">
            <div class="columns">
              <div class="column is-4">
                <button
                  class="button is-primary"
                  :disabled="
                    !brandingValue ||
                      loading ||
                      brandingColor === $store.getters['branding/getDefault']
                  "
                  @click="updateBrandingColor"
                >
                  Update Branding
                </button>
              </div>
              <div class="column">
                <button
                  class="button is-info tooltip is-tooltip-top"
                  data-tooltip="Reset Color"
                  :disabled="
                    !brandingValue ||
                      loading ||
                      brandingColor === $store.getters['branding/getDefault']
                  "
                  @click="updateBrandingColor($event, true)"
                >
                  <span class="icon">
                    <i class="fas fa-undo"></i>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="container">
          <h6 class="title is-6">Preview</h6>
          <div class="navbar-container">
            <nav class="navbar" role="navigation" aria-label="main navigation">
              <div class="navbar-brand">
                <a class="navbar-item" href="/" target="_blank">
                  <img v-if="logo" :src="logo" alt="ShareDocView Logo" />
                  <img
                    v-else
                    src="@/assets/sdv-white.png"
                    style="max-height: 3.75rem"
                    alt="ShareDocView Logo"
                  />
                </a>
              </div>
            </nav>
            <div class="navbar-line" :style="backgroundStyle"></div>
          </div>
        </div>
      </div>
    </div>
    <sdv-dialog v-model="confirmRemoval">
      <template #header>
        <p class="modal-card-title">Confirm Delete</p>
      </template>
      <template #footer>
        <button @click="deleteImage" class="button is-danger">Delete</button>
      </template>
      <template>
        <h5 class="title is-size-5" style="margin: 0.75rem 0">
          Are you sure you want to delete {{ toDelete.type }}?
        </h5>
      </template>
    </sdv-dialog>
  </div>
</template>
<script>
import { Chrome } from 'vue-color';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
export default {
  name: 'BrandingForm',
  components: {
    ValidationObserver,
    ValidationProvider,
    'color-picker': Chrome,
    SdvDialog: () => import('@/components/shared/SdvDialog')
  },
  props: ['userData'],
  data() {
    return {
      loading: false,
      disableWatermark: false,
      disableBranding: false,
      logoMaxWidth: 200,
      logoMaxHeight: 50,
      brandingColor:
        this.userData && this.userData?.account?.branding?.color
          ? this.userData?.account?.branding?.color
          : this.$store.getters['branding/getDefault'],
      customURL:
        this.userData && this.userData?.account?.branding?.customURL
          ? this.userData?.account?.branding?.customURL
          : '',
      previousCustomURL: this.userData && this.userData?.account?.branding?.customURL
          ? this.userData?.account?.branding?.customURL
          : '',
      confirmRemoval: false,
      toDelete: { type: null },
      user: this.userData || {},
      hasBrandingColor: Boolean(
        this.userData && this.userData?.account?.branding?.color
      )
    };
  },
  computed: {
    backgroundStyle() {
      if (this.hasBrandingColor) {
        return {
          'background-image': `linear-gradient(to right,
            ${this.getBrandingColor} 0%, ${this.getBrandingColor} 100%)`
        };
      } else {
        return {};
      }
    },
    getBrandingColor() {
      return this.brandingColor.hex || this.brandingColor;
    },
    logo() {
      return this.userData?.account?.branding?.logo;
    },
    watermarkValue() {
      return this.$store.getters['auth/getAccessLevel']?.watermark?.value;
    },
    brandingValue() {
      return this.$store.getters['auth/getAccessLevel']?.branding?.value;
    },
    isDifferentURL() {
      return this.previousCustomURL != this.customURL;
    }
  },
  watch: {
    brandingColor(value) {
      if (
        value.hex === this.$store.getters['branding/getDefault'] ||
        value === this.$store.getters['branding/getDefault'] ||
        !value
      ) {
        this.hasBrandingColor = false;
      } else {
        this.hasBrandingColor = true;
      }
    }
  },
  methods: {
    async updateBrandingColor(evt, reset) {
      this.loading = true;
      const color = {
        color: reset ? '' : this.getBrandingColor
      };
      try {
        const response = await this.$axios.put(
          this.$API.updateBrandingColor(this.user.account._id),
          color
        );
        this.user.account = response.data;
        this.$store.commit('auth/setUser', this.user);
        this.$notify({
          group: 'alert',
          title: 'SUCCESS!',
          text: 'Successfully saved your branding data.',
          type: 'success'
        });
      } catch (error) {
        this.$notify({
          group: 'alert',
          title: 'FAILED!',
          text: 'Unable to save branding data. Please try again later.',
          type: 'error'
        });
        console.error('Error: ', error);
      } finally {
        // Reset colors
        this.brandingColor =
          this.userData && this.userData?.account?.branding?.color
            ? this.userData?.account?.branding?.color
            : this.$store.getters['branding/getDefault'];
        this.loading = false;
      }
    },
    async updateCustomURL() {
      this.loading = true;
      const customURL = {customURL : this.customURL ? this.customURL : ''};
      try {
        const response = await this.$axios.put(
          this.$API.updateCustomURL(this.user.account._id),
          customURL
        );
        this.user.account = response.data;
        this.$store.commit('auth/setUser', this.user);
        this.$notify({
          group: 'alert',
          title: 'SUCCESS!',
          text: 'Successfully saved your branding data.',
          type: 'success'
        });
      } catch (error) {
        this.$notify({
          group: 'alert',
          title: 'FAILED!',
          text: 'Unable to save branding data. Please try again later.',
          type: 'error'
        });
        console.error('Error: ', error);
      } finally {
        // Reset colors
        this.customURL =
          this.userData && this.userData?.account?.branding?.customURL
            ? this.userData?.account?.branding?.customURL
            : '';
        this.previousCustomURL = this.customURL;
        this.loading = false;
      }
    },
    async uploadImage(file, type) {
      // eslint-disable-next-line
      var attributes = {};
      this.disableWatermark = type === 'watermark';
      this.disableBranding = type === 'branding';
      const formData = new FormData();
      formData.append(type, file);
      const options = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      // Remove whatever value the option had to make sure
      // it get's updated when the upload completes.
      const oldValue = this.user.account[type];
      this.user.account[type] = '';
      const url =
        type === 'watermark'
          ? this.$API.uploadWatermark
          : this.$API.uploadBranding;
      try {
        const response = await this.$axios.post(
          url(this.user.account._id),
          formData,
          options
        );
        this.user.account = response.data[0];
        this.$store.commit('auth/setUser', this.user);
        this.$notify({
          group: 'alert',
          title: 'SUCCESS!',
          text: 'Successfully uploaded.',
          type: 'success'
        });
        // Send customerly event according with the action executed, change water mark or change branding.
        if (type === 'watermark') {
          this.$customerly.event('change_watermark');
        } else {
          this.$customerly.event('change_branding');
        }
      } catch (error) {
        // Restore the old value in case of failure.
        this.user.account[type] = oldValue;
        this.$notify({
          group: 'alert',
          title: 'FAILED!',
          text: 'Unable to update image. Please check and try again.',
          type: 'error'
        });
        console.error('Error: ', error);
      } finally {
        this.disableWatermark = !!type === 'watermark';
        this.disableBranding = !!type === 'branding';
      }
    },
    async deleteImage() {
      const url =
        this.toDelete.type === 'watermark'
          ? this.$API.uploadWatermark
          : this.$API.uploadBranding;
      try {
        // remove customURL in branding
        if (this.toDelete.type === 'branding' && this.customURL) {
          this.customURL = '';
          this.updateCustomURL();
        }
        const response = await this.$axios.delete(url(this.user.account._id));
        this.confirmRemoval = false;
        this.user.account = response.data[0];
        this.$store.commit('auth/setUser', this.user);
        this.$notify({
          group: 'alert',
          title: 'SUCCESS!',
          text: 'Successfully Image deleted.',
          type: 'success'
        });
      } catch (error) {
        this.$notify({
          group: 'alert',
          title: 'FAILED!',
          text: 'Unable to delete image. Please check and try again.',
          type: 'error'
        });
        console.log('Error: ', error);
      }
    },
    validateImage(type) {
      const file = this.$refs[type].files[0];
      if (!file) {
        return;
      }
      const allowedTypes = {
        branding: ['image/jpg', 'image/jpeg', 'image/png'],
        watermark: ['image/png']
      };
      const MAX_SIZE = 200000; // 2 MB
      if (!allowedTypes[type].includes(file.type) || file.size > MAX_SIZE) {
        return this.$notify({
          group: 'alert',
          title: 'FAILED!',
          text: 'Unable to update image. Please check the file type and try again.',
          type: 'error'
        });
      }
      if (type === 'branding') {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = evt => {
          const image = new Image();
          image.src = evt.target.result;
          const _this = this;
          image.onload = function() {
            if (
              this.height > _this.logoMaxHeight ||
              this.width > _this.logoMaxWidth
            ) {
              _this.$notify({
                group: 'alert',
                title: 'FAILED!',
                text: 'Unable to update logo. Wrong dimensions.',
                type: 'error'
              });
              return;
            }
            _this.uploadImage(file, type);
          };
        };
      } else {
        this.uploadImage(file, type);
      }
    },
    openDeleteConfirmation(evt, type) {
      evt.preventDefault();
      this.toDelete = {
        type
      };
      this.confirmRemoval = true;
    }
  }
};
</script>
<style lang="scss" scoped>
.navbar {
  width: 100%;
  .navbar-item img {
    max-height: 1.75rem;
  }
}
.preview-container {
  margin-top: 1rem;
  .column {
    &.is-logo {
      img {
        width: 50%;
        height: auto;
      }
    }
    img {
      width: 100%;
      height: auto;

      &.watermark-preview {
        opacity: 0.15;
      }
    }
  }
}

.vc-chrome[disabled] {
  pointer-events: none;
  opacity: 0.5;
}

.file.disabled {
  pointer-events: none;
  opacity: 0.5;

  .file-input {
    display: none;
  }
}
</style>
